import { openRazorpayOrderCheckout } from './razorpayCheckout';
import { track } from './analytics';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

type PayNowStatus =
  | 'created'
  | 'authorized'
  | 'captured'
  | 'failed'
  | 'pending_webhook'
  | 'cancelled'
  | 'expired';

interface PayNowSessionResponse {
  intentId: string;
  status: PayNowStatus;
  amount?: number;
  currency?: string;
  keyId?: string;
  razorpayOrderId?: string;
  checkout: {
    keyId?: string;
    orderId?: string;
    amountSubunits?: number;
    currency?: string;
    name: string;
    description: string;
    prefill?: {
      email?: string;
      name?: string | null;
    };
    notes?: Record<string, string>;
  };
}

interface PayNowConfirmResponse {
  intentId: string;
  status: PayNowStatus;
  chargeAuthorizationId?: string | null;
  amountPaid?: number | null;
  currency?: string | null;
  errorCode?: string | null;
  errorMessage?: string | null;
}

interface PayNowStatusResponse {
  intentId: string;
  status: PayNowStatus;
  chargeAuthorizationId?: string | null;
  amountPaid?: number | null;
  currency?: string | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  updatedAt?: string | null;
}

interface PayNowFlowInput {
  featureKey: string;
  units: number;
  context: Record<string, string>;
  pricingSnapshot: {
    unitPrice: number;
    currency: string;
  };
  fallbackAmount: number;
  fallbackCurrency: string;
}

export interface PayNowFlowResult {
  intentId: string;
  chargeAuthorizationId: string;
  amount: number;
  currency: string;
}

function toErrorMessage(payload: unknown, fallback: string): string {
  if (!payload || typeof payload !== 'object') {
    return fallback;
  }

  const obj = payload as Record<string, unknown>;
  const explicit = obj.message || obj.error;
  if (typeof explicit === 'string' && explicit.trim()) {
    return explicit;
  }

  return fallback;
}

function toCapturedResult(
  response: PayNowConfirmResponse | PayNowStatusResponse,
  fallbackAmount: number,
  fallbackCurrency: string,
): PayNowFlowResult {
  const chargeAuthorizationId =
    typeof response.chargeAuthorizationId === 'string' && response.chargeAuthorizationId.trim()
      ? response.chargeAuthorizationId.trim()
      : null;

  if (!chargeAuthorizationId) {
    throw new Error('Payment captured but charge authorization was not generated.');
  }

  return {
    intentId: response.intentId,
    chargeAuthorizationId,
    amount: typeof response.amountPaid === 'number' ? response.amountPaid : fallbackAmount,
    currency: typeof response.currency === 'string' && response.currency.trim()
      ? response.currency
      : fallbackCurrency,
  };
}

function failedStatusToMessage(response: PayNowConfirmResponse | PayNowStatusResponse): string {
  if (response.errorMessage && response.errorCode) {
    return `${response.errorMessage} (${response.errorCode})`;
  }
  if (response.errorMessage) {
    return response.errorMessage;
  }
  if (response.errorCode) {
    return `Payment failed (${response.errorCode})`;
  }
  return 'Payment failed. Please try again.';
}

async function sleep(ms: number): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

async function getJson<T>(path: string, fallback: string): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
  });

  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(toErrorMessage(data, fallback));
  }

  return data as T;
}

async function postJson<T>(path: string, body: unknown, fallback: string): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(toErrorMessage(data, fallback));
  }

  return data as T;
}

async function pollPayNowStatus(intentId: string): Promise<PayNowStatusResponse> {
  const maxAttempts = 12;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    await sleep(1500);

    const status = await getJson<PayNowStatusResponse>(
      `/api/me/billing/pay-now/${intentId}/status`,
      'Unable to fetch payment status',
    );

    if (status.status === 'captured' || status.status === 'failed' || status.status === 'cancelled' || status.status === 'expired') {
      return status;
    }
  }

  throw new Error('Payment confirmation is taking longer than expected. Please retry in a moment.');
}

export async function runPayNowFlow(input: PayNowFlowInput): Promise<PayNowFlowResult> {
  const result = await runPayNowFlowUntracked(input);
  track('purchase', {
    transaction_id: result.intentId,
    value: result.amount,
    currency: result.currency,
    feature_key: input.featureKey,
    billing: 'pay_now',
  });
  return result;
}

async function runPayNowFlowUntracked(input: PayNowFlowInput): Promise<PayNowFlowResult> {
  const session = await postJson<PayNowSessionResponse>(
    '/api/me/billing/pay-now/session',
    {
      featureKey: input.featureKey,
      units: input.units,
      context: input.context,
      pricingSnapshot: {
        unitPrice: input.pricingSnapshot.unitPrice,
        currency: input.pricingSnapshot.currency,
      },
    },
    'Failed to create payment session',
  );

  const keyId = typeof session.checkout?.keyId === 'string' && session.checkout.keyId.trim()
    ? session.checkout.keyId.trim()
    : (typeof session.keyId === 'string' ? session.keyId.trim() : '');
  const orderId = typeof session.checkout?.orderId === 'string' && session.checkout.orderId.trim()
    ? session.checkout.orderId.trim()
    : (typeof session.razorpayOrderId === 'string' ? session.razorpayOrderId.trim() : '');
  const amountSubunits = typeof session.checkout?.amountSubunits === 'number' && Number.isFinite(session.checkout.amountSubunits)
    ? Math.max(1, Math.round(session.checkout.amountSubunits))
    : Math.max(1, Math.round((typeof session.amount === 'number' ? session.amount : input.fallbackAmount) * 100));
  const currency = typeof session.checkout?.currency === 'string' && session.checkout.currency.trim()
    ? session.checkout.currency
    : (typeof session.currency === 'string' && session.currency.trim() ? session.currency : input.fallbackCurrency);

  if (!keyId) {
    throw new Error('Payment gateway key is missing. Please contact support.');
  }
  if (!orderId) {
    throw new Error('Payment order is missing. Please retry.');
  }

  const checkoutResponse = await openRazorpayOrderCheckout({
    keyId,
    orderId,
    amountSubunits,
    currency,
    name: session.checkout?.name || 'Viral AI',
    description: session.checkout?.description || 'One-time payment',
    prefill: session.checkout?.prefill,
    notes: session.checkout?.notes,
  });

  const razorpayPaymentId = typeof checkoutResponse.razorpay_payment_id === 'string'
    ? checkoutResponse.razorpay_payment_id.trim()
    : '';
  const razorpayOrderId = typeof checkoutResponse.razorpay_order_id === 'string'
    ? checkoutResponse.razorpay_order_id.trim()
    : '';
  const razorpaySignature = typeof checkoutResponse.razorpay_signature === 'string'
    ? checkoutResponse.razorpay_signature.trim()
    : '';

  if (!razorpayPaymentId || !razorpayOrderId || !razorpaySignature) {
    throw new Error('Payment did not complete correctly. Please try again.');
  }

  const confirm = await postJson<PayNowConfirmResponse>(
    '/api/me/billing/pay-now/confirm',
    {
      intentId: session.intentId,
      razorpayPaymentId,
      razorpayOrderId,
      razorpaySignature,
    },
    'Failed to confirm payment',
  );

  if (confirm.status === 'captured') {
    return toCapturedResult(confirm, input.fallbackAmount, input.fallbackCurrency);
  }

  if (confirm.status === 'failed' || confirm.status === 'cancelled' || confirm.status === 'expired') {
    throw new Error(failedStatusToMessage(confirm));
  }

  if (confirm.status !== 'pending_webhook' && confirm.status !== 'authorized' && confirm.status !== 'created') {
    throw new Error('Unexpected payment state. Please retry in a moment.');
  }

  const status = await pollPayNowStatus(confirm.intentId);
  if (status.status === 'captured') {
    return toCapturedResult(status, input.fallbackAmount, input.fallbackCurrency);
  }

  throw new Error(failedStatusToMessage(status));
}
